import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { toast } from 'sonner';
import {
  Buildings, BookOpen, Clock, SpeakerHigh, Phone,
  CaretLeft, CaretRight, Check, CircleNotch, Microphone,
  Stop, Play, ArrowRight, Warning, Lightning,
  Brain, ChatText, CaretDown, CaretUp, Sparkle,
  UserCircle, EnvelopeSimple, PhoneCall, CalendarBlank,
  Target, ShieldCheck, Gear,
} from '@phosphor-icons/react';

const STEPS = [
  { id: 'business', label: 'Business basics', icon: Buildings },
  { id: 'knowledge', label: 'Knowledge', icon: BookOpen },
  { id: 'hours', label: 'Working hours', icon: Clock },
  { id: 'voice', label: 'Voice selection', icon: SpeakerHigh },
  { id: 'test', label: 'Test call', icon: Phone },
];

const BUSINESS_TYPES = [
  'Home Services', 'Healthcare', 'Legal', 'Real Estate',
  'Fitness & Wellness', 'Beauty & Salon', 'Restaurant',
  'Professional Services', 'Retail', 'Other',
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const VOICES = [
  { id: 'alloy', name: 'Alloy', desc: 'Warm and balanced', color: '#00E5C0' },
  { id: 'echo', name: 'Echo', desc: 'Clear and professional', color: '#3B82F6' },
  { id: 'nova', name: 'Nova', desc: 'Friendly and bright', color: '#F59E0B' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Calm and composed', color: '#A78BFA' },
];

const VoiceDemoWizard = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id || '';

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [demoStatus, setDemoStatus] = useState(null);
  const [configId, setConfigId] = useState(null);

  // Step data
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [knowledge, setKnowledge] = useState('');
  const [hours, setHours] = useState({
    monday: { enabled: true, open: '09:00', close: '17:00' },
    tuesday: { enabled: true, open: '09:00', close: '17:00' },
    wednesday: { enabled: true, open: '09:00', close: '17:00' },
    thursday: { enabled: true, open: '09:00', close: '17:00' },
    friday: { enabled: true, open: '09:00', close: '17:00' },
    saturday: { enabled: false, open: '10:00', close: '14:00' },
    sunday: { enabled: false, open: '10:00', close: '14:00' },
  });
  const [voiceId, setVoiceId] = useState('alloy');

  // Test call state
  const [sessionId, setSessionId] = useState(null);
  const [testActive, setTestActive] = useState(false);
  const [testTimer, setTestTimer] = useState(0);
  const [testStarting, setTestStarting] = useState(false);
  const [sessionCompleted, setSessionCompleted] = useState(false);

  // Post-call intelligence state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [processingTime, setProcessingTime] = useState(0);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [transcriptEntries, setTranscriptEntries] = useState([]);
  const [activatingWorker, setActivatingWorker] = useState(false);

  // S2S WebRTC refs
  const peerConnectionRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Load demo status
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const res = await fetchWithOrg(`${BACKEND_URL}/api/voice-demo/status`, {
          headers: { 'x-org-id': orgId },
        });
        if (res.ok) {
          const data = await res.json();
          setDemoStatus(data);
          if (data.config) {
            setBusinessName(data.config.business_name || '');
            setBusinessType(data.config.business_type || '');
            setKnowledge(data.config.knowledge || '');
            setVoiceId(data.config.voice_id || 'alloy');
            if (data.config.working_hours) {
              const merged = { ...hours };
              Object.entries(data.config.working_hours).forEach(([day, val]) => {
                if (merged[day]) merged[day] = { ...merged[day], enabled: true, ...val };
              });
              setHours(merged);
            }
            setConfigId(data.config.id);
          }
        }
      } catch (e) {
        console.warn('Failed to load demo status:', e);
      }
    };
    if (orgId) loadStatus();
  }, [orgId]);

  // Test call timer with auto-end at 3 minutes
  useEffect(() => {
    if (testActive) {
      timerRef.current = setInterval(() => {
        setTestTimer(t => {
          if (t >= 180) {
            endTestCall();
            return 180;
          }
          return t + 1;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [testActive]);

  // Cleanup on unmount — ensure no background WebRTC
  useEffect(() => {
    return () => {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const canProceed = () => {
    if (step === 0) return businessName.trim().length > 0 && businessType.length > 0;
    if (step === 1) return true; // Knowledge is optional
    if (step === 2) return true;
    if (step === 3) return voiceId.length > 0;
    return true;
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const workingHoursFiltered = {};
      Object.entries(hours).forEach(([day, val]) => {
        if (val.enabled) {
          workingHoursFiltered[day] = { open: val.open, close: val.close };
        }
      });

      const res = await fetchWithOrg(`${BACKEND_URL}/api/voice-demo/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-org-id': orgId },
        body: JSON.stringify({
          business_name: businessName.trim(),
          business_type: businessType,
          knowledge: knowledge.trim(),
          working_hours: workingHoursFiltered,
          voice_id: voiceId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setConfigId(data.config?.id);
        return true;
      }
      const err = await res.json();
      toast.error(err.detail || 'Failed to save configuration');
      return false;
    } catch {
      toast.error('Something went wrong. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (step === 3) {
      // Save config before going to test
      const saved = await saveConfig();
      if (!saved) return;
    }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const startTestCall = async () => {
    if (!configId) {
      toast.error('Save your configuration first.');
      return;
    }
    if (demoStatus && demoStatus.sessions_remaining <= 0) {
      toast.error('No demo sessions remaining this month. Upgrade for unlimited voice.');
      return;
    }
    setTestStarting(true);
    setTranscriptEntries([]);
    setAnalysis(null);
    setProcessingTime(0);
    try {
      // 1. Start demo session on backend
      const sessionRes = await fetchWithOrg(`${BACKEND_URL}/api/voice-demo/session/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-org-id': orgId },
        body: JSON.stringify({ config_id: configId }),
      });
      if (!sessionRes.ok) {
        const err = await sessionRes.json();
        const msg = typeof err.detail === 'object' ? err.detail.message : err.detail;
        toast.error(msg || 'Could not start demo call');
        return;
      }
      const sessionData = await sessionRes.json();
      const sid = sessionData.session.id;
      setSessionId(sid);
      setDemoStatus(prev => prev ? { ...prev, sessions_remaining: sessionData.sessions_remaining } : prev);

      // 2. Create WebRTC peer connection
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      // 3. Remote audio playback
      const audioEl = new Audio();
      audioEl.autoplay = true;
      audioRef.current = audioEl;
      pc.ontrack = (event) => {
        audioEl.srcObject = event.streams[0];
      };

      // 3b. Listen for data channel (transcript events from OpenAI Realtime)
      pc.ondatachannel = (event) => {
        const dc = event.channel;
        dc.onmessage = (msgEvent) => {
          try {
            const msg = JSON.parse(msgEvent.data);
            // Capture completed transcription events
            if (msg.type === 'conversation.item.input_audio_transcription.completed' && msg.transcript) {
              setTranscriptEntries(prev => [...prev, { role: 'user', text: msg.transcript }]);
            }
            if (msg.type === 'response.audio_transcript.done' && msg.transcript) {
              setTranscriptEntries(prev => [...prev, { role: 'assistant', text: msg.transcript }]);
            }
          } catch {}
        };
      };

      // 4. Get user mic and add to peer connection
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // 5. Create SDP offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // 6. Negotiate with demo endpoint (no credits consumed)
      const negotiateRes = await fetchWithOrg(`${BACKEND_URL}/api/voice-demo/negotiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-org-id': orgId },
        body: JSON.stringify({
          sdp_offer: offer.sdp,
          session_id: sid,
          voice: voiceId,
        }),
      });

      if (!negotiateRes.ok) {
        const err = await negotiateRes.json();
        toast.error(err.detail || 'Voice connection failed. Please try again.');
        pc.close();
        peerConnectionRef.current = null;
        // End the session since we couldn't connect
        await fetchWithOrg(`${BACKEND_URL}/api/voice-demo/session/${sid}/end`, {
          method: 'POST', headers: { 'x-org-id': orgId },
        });
        return;
      }

      const { sdp: sdpAnswer } = await negotiateRes.json();
      await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });

      setTestActive(true);
      setTestTimer(0);
      toast.success('Demo call connected');
    } catch (err) {
      console.error('Demo call error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow mic access and try again.');
      } else {
        toast.error('Failed to start demo call. Please try again.');
      }
      // Cleanup
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
    } finally {
      setTestStarting(false);
    }
  };

  const endTestCall = async () => {
    const currentSessionId = sessionId;
    const currentTranscript = [...transcriptEntries];
    setTestActive(false);
    setSessionCompleted(true);
    if (timerRef.current) clearInterval(timerRef.current);

    // Close WebRTC connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current = null;
    }

    // End session on backend
    if (currentSessionId) {
      try {
        await fetchWithOrg(`${BACKEND_URL}/api/voice-demo/session/${currentSessionId}/end`, {
          method: 'POST',
          headers: { 'x-org-id': orgId },
        });
      } catch {}
    }
    setSessionId(null);
    toast.info('Demo call ended — analyzing conversation...');

    // Trigger transcript analysis
    if (currentSessionId) {
      setAnalyzing(true);
      try {
        const res = await fetchWithOrg(`${BACKEND_URL}/api/voice-demo/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-org-id': orgId },
          body: JSON.stringify({
            session_id: currentSessionId,
            transcript_entries: currentTranscript,
            transcript: currentTranscript.map(e =>
              `${e.role === 'user' ? 'Customer' : 'AI Receptionist'}: ${e.text}`
            ).join('\n'),
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setAnalysis(data.analysis);
          setProcessingTime(data.processing_time_ms || 0);
        }
      } catch (err) {
        console.warn('Analysis failed:', err);
      } finally {
        setAnalyzing(false);
      }
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ───────────────────────────────
  // STEP RENDERERS
  // ───────────────────────────────

  const renderBusiness = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-xs font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Business name</label>
        <input
          type="text"
          value={businessName}
          onChange={e => setBusinessName(e.target.value)}
          placeholder="e.g. Bright Smile Dental"
          maxLength={100}
          className="w-full px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] text-sm placeholder-[#4B5563] focus:border-[#00E5C0]/30 focus:outline-none transition-colors"
          data-testid="demo-business-name"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Business type</label>
        <div className="grid grid-cols-2 gap-2">
          {BUSINESS_TYPES.map(type => (
            <button
              key={type}
              onClick={() => setBusinessType(type)}
              data-testid={`demo-btype-${type.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-3 py-2.5 rounded-xl text-sm text-left transition-all ${
                businessType === type
                  ? 'bg-[rgba(0,229,192,0.08)] border border-[#00E5C0]/25 text-[#00E5C0]'
                  : 'bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.05)]'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderKnowledge = () => (
    <div className="space-y-4">
      <p className="text-sm text-[#7D8896]">
        Tell your voice agent what it should know about your business. This helps it answer questions accurately.
      </p>
      <textarea
        value={knowledge}
        onChange={e => setKnowledge(e.target.value)}
        placeholder="e.g. We offer general dentistry, cosmetic procedures, and orthodontics. Appointments are typically 30-60 minutes. We accept most insurance plans..."
        maxLength={2000}
        rows={8}
        className="w-full px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] text-sm placeholder-[#4B5563] focus:border-[#00E5C0]/30 focus:outline-none transition-colors resize-none"
        data-testid="demo-knowledge"
      />
      <div className="flex justify-between text-[10px] text-[#4B5563]">
        <span>Plain text only in demo mode</span>
        <span>{knowledge.length} / 2,000</span>
      </div>
    </div>
  );

  const renderHours = () => (
    <div className="space-y-3">
      <p className="text-sm text-[#7D8896] mb-4">Set when your business is available.</p>
      {DAYS.map(day => (
        <div key={day} className="flex items-center gap-3">
          <button
            onClick={() => setHours(h => ({ ...h, [day]: { ...h[day], enabled: !h[day].enabled } }))}
            data-testid={`demo-day-${day}`}
            className={`w-12 text-xs font-semibold py-1.5 rounded-lg transition-colors ${
              hours[day].enabled
                ? 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0]'
                : 'bg-[rgba(255,255,255,0.03)] text-[#4B5563]'
            }`}
          >
            {DAY_LABELS[day]}
          </button>
          {hours[day].enabled ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="time"
                value={hours[day].open}
                onChange={e => setHours(h => ({ ...h, [day]: { ...h[day], open: e.target.value } }))}
                className="px-3 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] text-xs focus:outline-none"
              />
              <span className="text-[#4B5563] text-xs">to</span>
              <input
                type="time"
                value={hours[day].close}
                onChange={e => setHours(h => ({ ...h, [day]: { ...h[day], close: e.target.value } }))}
                className="px-3 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] text-xs focus:outline-none"
              />
            </div>
          ) : (
            <span className="text-xs text-[#4B5563]">Closed</span>
          )}
        </div>
      ))}
    </div>
  );

  const renderVoice = () => (
    <div className="space-y-4">
      <p className="text-sm text-[#7D8896] mb-2">Choose how your voice agent sounds.</p>
      <div className="grid grid-cols-2 gap-3">
        {VOICES.map(v => (
          <button
            key={v.id}
            onClick={() => setVoiceId(v.id)}
            data-testid={`demo-voice-${v.id}`}
            className={`p-4 rounded-xl text-left transition-all ${
              voiceId === v.id
                ? 'bg-[rgba(0,229,192,0.06)] border-2 border-[#00E5C0]/30'
                : 'bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)]'
            }`}
          >
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: v.color }} />
              <span className="text-sm font-semibold text-white">{v.name}</span>
            </div>
            <p className="text-xs text-[#7D8896]">{v.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );

  const handleActivateWorker = async () => {
    setActivatingWorker(true);
    try {
      // Navigate to the agent marketplace to create an enquiry worker
      navigate('/agents?activate=enquiry&source=voice-demo');
    } catch {
      toast.error('Could not activate worker. Please try again.');
    } finally {
      setActivatingWorker(false);
    }
  };

  const SENTIMENT_CONFIG = {
    positive: { color: '#00E5C0', bg: 'rgba(0,229,192,0.08)', label: 'Positive' },
    neutral: { color: '#7D8896', bg: 'rgba(107,118,133,0.08)', label: 'Neutral' },
    negative: { color: '#EF4444', bg: 'rgba(239,68,68,0.08)', label: 'Negative' },
  };

  const URGENCY_CONFIG = {
    low: { color: '#7D8896', label: 'Low' },
    medium: { color: '#F59E0B', label: 'Medium' },
    high: { color: '#EF4444', label: 'High' },
  };

  const renderTest = () => {
    const remaining = demoStatus?.sessions_remaining ?? 3;

    return (
      <div className="space-y-5">
        {/* Demo mode label */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] w-fit" data-testid="demo-mode-label">
          <div className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
          <span className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">Demo mode</span>
        </div>

        {/* Session counter */}
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)]">
          <Lightning size={16} className="text-[#00E5C0]" weight="fill" />
          <span className="text-xs text-[#B0B8C4]">
            <span className="text-white font-semibold">{remaining}</span> demo session{remaining !== 1 ? 's' : ''} remaining this month
          </span>
        </div>

        {remaining <= 0 && !testActive && !sessionCompleted && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-[rgba(245,158,11,0.06)] border border-[rgba(245,158,11,0.12)]" data-testid="demo-limit-banner">
            <Warning size={16} className="text-[#F59E0B] mt-0.5 shrink-0" weight="fill" />
            <div>
              <p className="text-sm text-[#F0F4F8] font-medium">You've reached your demo limit</p>
              <p className="text-xs text-[#7D8896] mt-1">Upgrade to use voice with real calls.</p>
              <button
                onClick={() => navigate('/pricing')}
                className="mt-2 text-xs font-semibold text-[#00E5C0] hover:underline flex items-center gap-1"
                data-testid="demo-upgrade-btn"
              >
                Upgrade to use voice with real calls <ArrowRight size={12} />
              </button>
            </div>
          </div>
        )}

        {/* ═══ INTELLIGENCE SUMMARY (post-call) ═══ */}
        {sessionCompleted && !testActive && (analyzing || analysis) && (
          <div className="space-y-4" data-testid="demo-intelligence-section">
            {analyzing ? (
              <div className="flex flex-col items-center py-8 px-4 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]" data-testid="demo-analyzing">
                <CircleNotch size={28} className="text-[#00E5C0] animate-spin mb-3" />
                <p className="text-sm text-[#F0F4F8] font-medium">Analyzing your conversation...</p>
                <p className="text-xs text-[#4B5563] mt-1">Extracting intent, sentiment, and key information</p>
              </div>
            ) : analysis && (
              <>
                {/* Processing speed banner */}
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.08)]" data-testid="demo-speed-banner">
                  <Sparkle size={14} className="text-[#00E5C0]" weight="fill" />
                  <span className="text-xs text-[#B0B8C4]">
                    Responded instantly — no missed leads
                  </span>
                </div>

                {/* Intelligence card */}
                <div className="rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] overflow-hidden" data-testid="demo-intelligence-card">
                  <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.08)] flex items-center gap-2">
                    <Brain size={16} className="text-[#00E5C0]" weight="duotone" />
                    <span className="text-sm font-semibold text-[#F0F4F8]">Call Intelligence</span>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Summary */}
                    {analysis.summary && (
                      <p className="text-sm text-[#B0B8C4] leading-relaxed" data-testid="demo-summary">
                        {analysis.summary}
                      </p>
                    )}

                    {/* Classification badges */}
                    <div className="flex flex-wrap gap-2" data-testid="demo-badges">
                      {/* Intent */}
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[rgba(59,130,246,0.08)] border border-[rgba(59,130,246,0.15)]">
                        <Target size={12} className="text-[#3B82F6]" weight="fill" />
                        <span className="text-[11px] font-medium text-[#3B82F6]">{analysis.intent_label || analysis.intent}</span>
                      </div>
                      {/* Sentiment */}
                      {analysis.sentiment && (
                        <div
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border"
                          style={{
                            backgroundColor: SENTIMENT_CONFIG[analysis.sentiment]?.bg || SENTIMENT_CONFIG.neutral.bg,
                            borderColor: `${SENTIMENT_CONFIG[analysis.sentiment]?.color || '#7D8896'}25`,
                          }}
                        >
                          <ShieldCheck size={12} weight="fill" style={{ color: SENTIMENT_CONFIG[analysis.sentiment]?.color }} />
                          <span className="text-[11px] font-medium" style={{ color: SENTIMENT_CONFIG[analysis.sentiment]?.color }}>
                            {SENTIMENT_CONFIG[analysis.sentiment]?.label || 'Neutral'}
                          </span>
                        </div>
                      )}
                      {/* Urgency */}
                      {analysis.urgency && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)]">
                          <Lightning size={12} weight="fill" style={{ color: URGENCY_CONFIG[analysis.urgency]?.color || '#7D8896' }} />
                          <span className="text-[11px] font-medium" style={{ color: URGENCY_CONFIG[analysis.urgency]?.color }}>
                            {URGENCY_CONFIG[analysis.urgency]?.label} urgency
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Extracted info */}
                    {analysis.extracted_info && Object.values(analysis.extracted_info).some(v => v) && (
                      <div className="space-y-2" data-testid="demo-extracted-info">
                        <p className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">Extracted information</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {analysis.extracted_info.caller_name && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)]">
                              <UserCircle size={14} className="text-[#7D8896]" />
                              <div>
                                <p className="text-[10px] text-[#4B5563]">Name</p>
                                <p className="text-xs text-[#F0F4F8]">{analysis.extracted_info.caller_name}</p>
                              </div>
                            </div>
                          )}
                          {analysis.extracted_info.phone_number && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)]">
                              <PhoneCall size={14} className="text-[#7D8896]" />
                              <div>
                                <p className="text-[10px] text-[#4B5563]">Phone</p>
                                <p className="text-xs text-[#F0F4F8]">{analysis.extracted_info.phone_number}</p>
                              </div>
                            </div>
                          )}
                          {analysis.extracted_info.email && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)]">
                              <EnvelopeSimple size={14} className="text-[#7D8896]" />
                              <div>
                                <p className="text-[10px] text-[#4B5563]">Email</p>
                                <p className="text-xs text-[#F0F4F8]">{analysis.extracted_info.email}</p>
                              </div>
                            </div>
                          )}
                          {analysis.extracted_info.service_requested && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)]">
                              <Gear size={14} className="text-[#7D8896]" />
                              <div>
                                <p className="text-[10px] text-[#4B5563]">Service</p>
                                <p className="text-xs text-[#F0F4F8]">{analysis.extracted_info.service_requested}</p>
                              </div>
                            </div>
                          )}
                          {analysis.extracted_info.preferred_time && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)]">
                              <CalendarBlank size={14} className="text-[#7D8896]" />
                              <div>
                                <p className="text-[10px] text-[#4B5563]">Preferred time</p>
                                <p className="text-xs text-[#F0F4F8]">{analysis.extracted_info.preferred_time}</p>
                              </div>
                            </div>
                          )}
                          {analysis.extracted_info.key_details && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)] sm:col-span-2">
                              <ChatText size={14} className="text-[#7D8896]" />
                              <div>
                                <p className="text-[10px] text-[#4B5563]">Key details</p>
                                <p className="text-xs text-[#F0F4F8]">{analysis.extracted_info.key_details}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Suggested actions */}
                    {analysis.suggested_actions?.length > 0 && (
                      <div className="space-y-2" data-testid="demo-suggested-actions">
                        <p className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">Suggested next steps</p>
                        <div className="space-y-1.5">
                          {analysis.suggested_actions.map((action, i) => (
                            <div key={`suggested-${i}-${action.slice(0,20)}`} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[rgba(0,229,192,0.03)]">
                              <ArrowRight size={10} className="text-[#00E5C0] mt-0.5 shrink-0" />
                              <span className="text-xs text-[#B0B8C4]">{action}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Collapsible transcript */}
                {transcriptEntries.length > 0 && (
                  <div className="rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] overflow-hidden" data-testid="demo-transcript-panel">
                    <button
                      onClick={() => setTranscriptExpanded(!transcriptExpanded)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                      data-testid="demo-transcript-toggle"
                    >
                      <div className="flex items-center gap-2">
                        <ChatText size={14} className="text-[#7D8896]" />
                        <span className="text-xs font-medium text-[#B0B8C4]">Full transcript</span>
                        <span className="text-[10px] text-[#4B5563]">({transcriptEntries.length} messages)</span>
                      </div>
                      {transcriptExpanded ? <CaretUp size={12} className="text-[#7D8896]" /> : <CaretDown size={12} className="text-[#7D8896]" />}
                    </button>
                    {transcriptExpanded && (
                      <div className="px-4 pb-4 space-y-2 border-t border-[rgba(255,255,255,0.04)]">
                        <div className="pt-3 space-y-2 max-h-60 overflow-y-auto">
                          {transcriptEntries.map((entry, i) => (
                            <div key={`transcript-${entry.role}-${i}`} className={`flex gap-2 ${entry.role === 'user' ? '' : 'flex-row-reverse'}`}>
                              <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs ${
                                entry.role === 'user'
                                  ? 'bg-[rgba(59,130,246,0.08)] text-[#93C5FD]'
                                  : 'bg-[rgba(0,229,192,0.06)] text-[#6EE7B7]'
                              }`}>
                                <span className="text-[10px] font-semibold block mb-0.5 opacity-60">
                                  {entry.role === 'user' ? 'Customer' : 'AI Receptionist'}
                                </span>
                                {entry.text}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ CONVERSION BRIDGE ═══ */}
                <div className="rounded-xl bg-gradient-to-b from-[rgba(0,229,192,0.06)] to-[rgba(0,229,192,0.02)] border border-[rgba(0,229,192,0.12)] p-5" data-testid="demo-conversion-bridge">
                  {/* Friction-reduction line */}
                  <p className="text-xs text-[#7D8896] mb-4" data-testid="demo-friction-line">
                    No setup required — your AI is already ready to do this
                  </p>

                  <button
                    onClick={handleActivateWorker}
                    disabled={activatingWorker}
                    data-testid="demo-activate-worker-cta"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#00E5C0] text-[#0A0E14] text-sm font-semibold hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
                  >
                    {activatingWorker ? (
                      <><CircleNotch size={16} className="animate-spin" /> Activating...</>
                    ) : (
                      <>Start handling enquiries like this automatically</>
                    )}
                  </button>
                  <p className="text-center text-[11px] text-[#7D8896] mt-2">
                    Takes 30 seconds to activate
                  </p>

                  {/* Outcome reinforcement */}
                  <div className="mt-4 space-y-1.5" data-testid="demo-outcome-reinforcement">
                    <p className="text-xs font-medium text-[#B0B8C4]">Your AI will:</p>
                    <div className="flex items-center gap-2">
                      <Check size={12} className="text-[#00E5C0] shrink-0" weight="bold" />
                      <span className="text-xs text-[#7D8896]">respond instantly to new enquiries</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check size={12} className="text-[#00E5C0] shrink-0" weight="bold" />
                      <span className="text-xs text-[#7D8896]">follow up automatically</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check size={12} className="text-[#00E5C0] shrink-0" weight="bold" />
                      <span className="text-xs text-[#7D8896]">notify you only when needed</span>
                    </div>
                  </div>

                  <p className="text-center text-[10px] text-[#4B5563] mt-4">
                    Upgrade to Professional for live phone numbers, unlimited calls, and deployment.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Post-session fallback (no analysis yet, e.g. empty transcript) */}
        {sessionCompleted && !testActive && !analyzing && !analysis && (
          <div className="p-4 rounded-xl bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.1)]" data-testid="demo-success-message">
            <p className="text-sm text-[#F0F4F8] font-medium mb-1">
              This is exactly how your AI will answer real calls.
            </p>
            <p className="text-xs text-[#7D8896] mb-3">
              Connect a phone number to start handling calls automatically.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="text-xs font-semibold text-[#00E5C0] hover:underline flex items-center gap-1"
              data-testid="demo-go-live-cta"
            >
              Connect your phone number to go live <ArrowRight size={12} />
            </button>
          </div>
        )}

        {/* Test call UI */}
        <div className="flex flex-col items-center py-6">
          {testActive ? (
            <>
              <div className="relative w-28 h-28 mb-5">
                <div className="absolute inset-0 rounded-full bg-[rgba(0,229,192,0.1)] animate-ping" style={{ animationDuration: '2s' }} />
                <div className="absolute inset-2 rounded-full bg-[rgba(0,229,192,0.15)]" />
                <div className="absolute inset-4 rounded-full bg-[#0A0E14] flex items-center justify-center">
                  <Microphone size={32} className="text-[#00E5C0]" weight="fill" />
                </div>
              </div>
              <p className="text-sm text-[#F0F4F8] font-medium mb-1">Listening...</p>
              <p className="text-2xl font-bold text-white tabular-nums mb-1" data-testid="demo-timer">
                {formatTime(testTimer)} <span className="text-sm font-normal text-[#4B5563]">/ 3:00</span>
              </p>
              <p className="text-[10px] text-[#4B5563] mb-5">Speak naturally — your AI agent is listening</p>
              <button
                onClick={endTestCall}
                data-testid="demo-end-call"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#EF4444] text-white text-sm font-semibold hover:bg-[#DC2626] transition-colors"
              >
                <Stop size={16} weight="fill" /> End call
              </button>

              {/* Live transcript during call */}
              {transcriptEntries.length > 0 && (
                <div className="mt-5 w-full max-w-md space-y-1.5 max-h-32 overflow-y-auto" data-testid="demo-live-transcript">
                  {transcriptEntries.slice(-4).map((entry, i) => (
                    <div key={`live-${entry.role}-${i}`} className="flex gap-2 items-start">
                      <span className={`text-[10px] font-semibold shrink-0 w-14 text-right ${entry.role === 'user' ? 'text-[#3B82F6]' : 'text-[#00E5C0]'}`}>
                        {entry.role === 'user' ? 'You' : 'AI'}
                      </span>
                      <span className="text-xs text-[#7D8896]">{entry.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : !sessionCompleted ? (
            <>
              <div className="w-28 h-28 rounded-full bg-[rgba(0,229,192,0.06)] flex items-center justify-center mb-5">
                <Phone size={36} className="text-[#00E5C0]" weight="duotone" />
              </div>
              <p className="text-sm text-[#F0F4F8] font-medium mb-1">Ready to test</p>
              <p className="text-xs text-[#7D8896] mb-5 max-w-xs text-center">
                Start a {demoStatus?.max_minutes_per_session || 3}-minute live demo. Speak to your AI voice agent through your microphone.
              </p>
              <button
                onClick={startTestCall}
                disabled={testStarting || remaining <= 0}
                data-testid="demo-start-call"
                className={`inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold transition-colors ${
                  remaining <= 0
                    ? 'bg-[rgba(255,255,255,0.05)] text-[#7D8896] cursor-not-allowed'
                    : 'bg-[#00E5C0] text-[#0A0E14] hover:bg-[#00CCB0]'
                }`}
              >
                {testStarting ? (
                  <><CircleNotch size={16} className="animate-spin" /> Connecting...</>
                ) : (
                  <><Play size={16} weight="fill" /> Start demo call</>
                )}
              </button>
            </>
          ) : (
            /* Session completed — show "Try again" button below analysis */
            <button
              onClick={() => {
                setSessionCompleted(false);
                setAnalysis(null);
                setTranscriptEntries([]);
                setProcessingTime(0);
              }}
              data-testid="demo-try-again"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-medium text-[#B0B8C4] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
            >
              <Play size={12} /> Try another demo call
            </button>
          )}
        </div>

        {/* Upgrade nudge — only before first call */}
        {!testActive && !sessionCompleted && (
          <div className="text-center pt-2">
            <p className="text-xs text-[#4B5563]">
              Ready for real calls? <button onClick={() => navigate('/pricing')} className="text-[#00E5C0] hover:underline">Upgrade to Professional</button> for telephony, unlimited sessions, and deployment.
            </p>
          </div>
        )}
      </div>
    );
  };

  const stepRenderers = [renderBusiness, renderKnowledge, renderHours, renderVoice, renderTest];

  return (
    <div className="min-h-screen bg-[#0A0E14] flex flex-col" data-testid="voice-demo-wizard">
      {/* Header */}
      <div className="border-b border-[rgba(255,255,255,0.08)] bg-[#0D1117]">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-white">Voice Demo</h1>
            <p className="text-xs text-[#7D8896]">Try your AI voice agent — no setup required</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors"
            data-testid="demo-close"
          >
            Exit demo
          </button>
        </div>
      </div>

      {/* Step indicator */}
      <div className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-2xl mx-auto px-6 py-3 flex items-center gap-1">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isCompleted = i < step;
            return (
              <button
                key={s.id}
                onClick={() => i <= step && setStep(i)}
                data-testid={`demo-step-${s.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isActive
                    ? 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0]'
                    : isCompleted
                    ? 'text-[#7D8896] hover:text-[#B0B8C4]'
                    : 'text-[#3B4350] cursor-default'
                }`}
              >
                {isCompleted ? <Check size={12} weight="bold" className="text-[#00E5C0]" /> : <Icon size={12} />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="text-xl font-semibold text-white mb-1">{STEPS[step].label}</h2>
              <p className="text-xs text-[#4B5563] mb-6">Step {step + 1} of {STEPS.length}</p>
              {stepRenderers[step]()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer nav */}
      {step < 4 && (
        <div className="border-t border-[rgba(255,255,255,0.08)]">
          <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="demo-back"
              className={`flex items-center gap-1.5 text-sm ${
                step === 0 ? 'text-[#3B4350] cursor-not-allowed' : 'text-[#B0B8C4] hover:text-white'
              } transition-colors`}
            >
              <CaretLeft size={14} /> Back
            </button>
            <button
              onClick={handleNext}
              disabled={!canProceed() || saving}
              data-testid="demo-next"
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                canProceed() && !saving
                  ? 'bg-[#00E5C0] text-[#0A0E14] hover:bg-[#00CCB0]'
                  : 'bg-[rgba(255,255,255,0.05)] text-[#4B5563] cursor-not-allowed'
              }`}
            >
              {saving ? (
                <><CircleNotch size={14} className="animate-spin" /> Saving...</>
              ) : (
                <>Continue <CaretRight size={14} /></>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceDemoWizard;
